interface BoundingBox {
    id: string;
    type: "box" | "polygon";
    label: string;
    x: number;
    y: number;
    width: number;
    height: number;
}
/*
TODO: eventually we want more granular activity tracking at the bounding box level

interface BoxActivity {
    id: BoundingBox["id"];
    activity: "added" | "removed" | "updated";
    actor: "<user_id>" | "assisted";
    updateData?: Array<{
        property: keyof BoundingBox;
        originalValue: BoundingBox[keyof BoundingBox];
        newValue: BoundingBox[keyof BoundingBox];
    }>;
}
*/

interface LabelingSession {
    existingBoxes: BoundingBox[];
    boxesAdded: Array<BoundingBox["id"]>;
    boxesRemoved: Array<BoundingBox["id"]>;
    boxesUpdated: Array<BoundingBox["id"]>;
}

export interface LabelingSessionState {
    labelingSession: LabelingSession | undefined;
    getLabelingSession: () => LabelingSession;
    updateLabelingSession: ({ boxes }: { boxes: BoundingBox[] }) => any;
    resetLabelingSession: ({ existingBoxes }: { existingBoxes?: BoundingBox[] }) => any;
    removeBox: ({ boxId }: { boxId: BoundingBox["id"] }) => any;
}

function getEmptyLabelingSession(overrides?: Partial<LabelingSession>): LabelingSession {
    return {
        existingBoxes: overrides?.existingBoxes || [],
        boxesAdded: overrides?.boxesAdded || [],
        boxesRemoved: overrides?.boxesRemoved || [],
        boxesUpdated: overrides?.boxesUpdated || []
    };
}

function boxWasChanged({
    originalBox,
    newBox
}: {
    originalBox: BoundingBox;
    newBox: BoundingBox;
}): boolean {
    if (originalBox.id !== newBox.id) {
        return false;
    }
    return (
        originalBox.label !== newBox.label ||
        originalBox.x !== newBox.x ||
        originalBox.y !== newBox.y ||
        originalBox.width !== newBox.width ||
        originalBox.height !== newBox.height ||
        originalBox.type !== newBox.type
    );
}

function appendBoxIds(
    boxIds: Array<BoundingBox["id"]>,
    boxes: BoundingBox[]
): Array<BoundingBox["id"]> {
    const newBoxIds = boxes.map((box) => box.id);
    return Array.from(new Set([...boxIds, ...newBoxIds])).filter((x) => x);
}

function labelingSessionReducer(set, get): LabelingSessionState {
    return {
        // values
        labelingSession: getEmptyLabelingSession(),

        // getters
        getLabelingSession() {
            return get().labelingSession;
        },

        //mutators

        resetLabelingSession({ existingBoxes = [] }: { existingBoxes?: BoundingBox[] } = {}) {
            return set(
                () => ({ labelingSession: getEmptyLabelingSession({ existingBoxes }) }),
                false,
                "resetLabelingSession"
            );
        },

        removeBox({ boxId }: { boxId: BoundingBox["id"] }) {
            return set(
                () => ({
                    labelingSession: {
                        ...get().labelingSession,
                        boxesAdded: get().labelingSession.boxesAdded,
                        boxesUpdated: get().labelingSession.boxesUpdated,
                        boxesRemoved: [...get().labelingSession.boxesRemoved, boxId]
                    }
                }),
                false,
                "removeBoxFromLabelingSession"
            );
        },

        updateLabelingSession({ boxes }: { boxes: BoundingBox[] }) {
            const labelingSession = get().labelingSession;
            if (!labelingSession) {
                return set(
                    () => ({ labelingSession: getEmptyLabelingSession() }),
                    false,
                    "updateLabelingSession"
                );
            }

            const existingBoxes = get().labelingSession.existingBoxes || [];

            const boxesAdded = boxes.filter(
                (newBox) => !existingBoxes.some((existingBox) => existingBox.id === newBox.id)
            );
            const boxesRemoved = existingBoxes.filter(
                (existingBox) => !boxes.some((newBox) => newBox.id === existingBox.id)
            );
            const boxesUpdated = boxes.filter((newBox) => {
                const existingBox = existingBoxes.find((box) => box.id === newBox.id);
                return existingBox && boxWasChanged({ originalBox: existingBox, newBox });
            });

            const updatedLabelingSession = {
                ...labelingSession,
                boxesAdded: appendBoxIds(labelingSession.boxesAdded, boxesAdded),
                boxesRemoved: appendBoxIds(labelingSession.boxesRemoved, boxesRemoved),
                boxesUpdated: appendBoxIds(labelingSession.boxesUpdated, boxesUpdated)
            };

            return set(
                () => ({ labelingSession: updatedLabelingSession }),
                false,
                "updateLabelingSession"
            );
        }
    };
}

export function buildLabelingSessionStore(set, get, api) {
    return { ...labelingSessionReducer(set, get) };
}
